/**
 * LocalStorage Utility Functions
 * Provides global functions for managing localStorage with error handling and type safety
 */

import { AUTH_TOKEN_KEYS } from "../../constants/common";


/**
 * Set an item in localStorage
 * @param {string} key - The key to store the value under
 * @param {any} value - The value to store (will be JSON stringified)
 * @returns {boolean} True if successful, false otherwise
 */
export const setLocalItem = (key, value) => {
  try {
    const stringValue = typeof value === 'string' ? value : JSON.stringify(value);
    localStorage.setItem(key, stringValue);
    
    // Dispatch custom event for accessToken changes (same-tab detection)
    if (key === AUTH_TOKEN_KEYS.ACCESS_TOKEN && typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('localStorageChange', {
        detail: { key, value: stringValue, action: 'set' }
      }));
    }
    
    return true;
  } catch (error) {
    return false;
  }
};

/**
 * Get an item from localStorage
 * @param {string} key - The key to retrieve
 * @param {any} defaultValue - Default value to return if key doesn't exist
 * @returns {any} The stored value or default value
 */
export const getLocalItem = (key, defaultValue = null) => {
  try {
    const item = localStorage.getItem(key);
    if (item === null) {
      return defaultValue;
    }

    // Try to parse as JSON, if it fails return the raw string
    try {
      return JSON.parse(item);
    } catch {
      return item;
    }
  } catch (error) {
    return defaultValue;
  }
};

/**
 * Remove an item from localStorage
 * @param {string} key - The key to remove
 * @returns {boolean} True if successful, false otherwise
 */
export const removeLocalItem = (key) => {
  try {
    localStorage.removeItem(key);
    
    // Dispatch custom event for accessToken changes (same-tab detection)
    if (key === AUTH_TOKEN_KEYS.ACCESS_TOKEN && typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('localStorageChange', {
        detail: { key, value: null, action: 'remove' }
      }));
    }
    
    return true;
  } catch (error) {
    return false;
  }
};

/**
 * Clear all items from localStorage
 * @returns {boolean} True if successful, false otherwise
 */
export const clearLocalStorage = () => {
  try {
    localStorage.clear();
    return true;
  } catch (error) {
    return false;
  }
};

/**
 * Get all keys from localStorage
 * @returns {string[]} Array of all keys in localStorage
 */
export const getLocalKeys = () => {
  try {
    return Object.keys(localStorage);
  } catch (error) {
    return [];
  }
};

/**
 * Get the number of items in localStorage
 * @returns {number} Number of items in localStorage
 */
export const getLocalLength = () => {
  try {
    return localStorage.length;
  } catch (error) {
    return 0;
  }
};

/**
 * Check if a key exists in localStorage
 * @param {string} key - The key to check
 * @returns {boolean} True if key exists, false otherwise
 */
export const hasLocalKey = (key) => {
  try {
    return localStorage.getItem(key) !== null;
  } catch (error) {
    return false;
  }
};

/**
 * Get all items from localStorage as an object
 * @returns {Object} Object containing all localStorage key-value pairs
 */
export const getAllLocalItems = () => {
  try {
    const items = {};
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key) {
        items[key] = getLocalItem(key);
      }
    }
    return items;
  } catch (error) {
    return {};
  }
};

/**
 * Set multiple items in localStorage at once
 * @param {Object} items - Object containing key-value pairs to store
 * @returns {boolean} True if all items were set successfully, false otherwise
 */
export const setMultipleLocalItems = (items) => {
  try {
    const keys = Object.keys(items);
    let allSuccess = true;

    keys.forEach(key => {
      const success = setLocalItem(key, items[key]);
      if (!success) {
        allSuccess = false;
      }
    });

    return allSuccess;
  } catch (error) {
    return false;
  }
};

/**
 * Remove multiple items from localStorage at once
 * @param {string[]} keys - Array of keys to remove
 * @returns {boolean} True if all items were removed successfully, false otherwise
 */
export const removeMultipleLocalItems = (keys) => {
  try {
    let allSuccess = true;

    keys.forEach(key => {
      const success = removeLocalItem(key);
      if (!success) {
        allSuccess = false;
      }
    });

    return allSuccess;
  } catch (error) {
    return false;
  }
};

// Make functions available globally
if (typeof window !== 'undefined') {
  window.localStorageUtils = {
    set: setLocalItem,
    get: getLocalItem,
    remove: removeLocalItem,
    clear: clearLocalStorage,
    keys: getLocalKeys,
    length: getLocalLength,
    has: hasLocalKey,
    getAll: getAllLocalItems,
    setMultiple: setMultipleLocalItems,
    removeMultiple: removeMultipleLocalItems
  };
}

// Default export with all functions
export default {
  set: setLocalItem,
  get: getLocalItem,
  remove: removeLocalItem,
  clear: clearLocalStorage,
  keys: getLocalKeys,
  length: getLocalLength,
  has: hasLocalKey,
  getAll: getAllLocalItems,
  setMultiple: setMultipleLocalItems,
  removeMultiple: removeMultipleLocalItems
};
